import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { suppliesAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, ArrowRight, PackageCheck } from 'lucide-react';

/**
 * OverdueLoansTile — KR dashboard tile that reveals how many active
 * equipment loans are past their scheduled return date, plus a
 * one-tap link to the standalone /supplies operations app.
 *
 * Renders nothing when the caller lacks KR access or the count is 0
 * (we return a subtle "All good" card so the tile still shows up on
 * the dashboard for peace-of-mind and to teach admins where /supplies
 * lives).
 */
export default function OverdueLoansTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    suppliesAPI.overdueLoansCount()
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData({ count: 0, items: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const count = data?.count ?? 0;
  const isAlert = count > 0;

  return (
    <Link to="/supplies" data-testid="overdue-loans-tile">
      <Card
        className={`transition-all hover:shadow-md cursor-pointer border-l-4 ${
          isAlert ? 'border-l-rose-500 bg-rose-50/40' : 'border-l-emerald-500'
        }`}
      >
        <CardContent className="p-4 flex items-center gap-4">
          <div
            className={`h-12 w-12 rounded-lg flex items-center justify-center ${
              isAlert ? 'bg-rose-500 text-white' : 'bg-emerald-500 text-white'
            }`}
          >
            {isAlert ? <AlertTriangle size={22} /> : <PackageCheck size={22} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              Equipment Loans
            </div>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-bold ${isAlert ? 'text-rose-700' : 'text-slate-900'}`}>
                {loading ? '—' : count}
              </span>
              <span className="text-sm text-slate-600">
                {isAlert ? (count === 1 ? 'overdue return' : 'overdue returns') : 'nothing overdue'}
              </span>
            </div>
            {isAlert && data?.items?.[0] && (
              <div className="text-xs text-rose-700 truncate mt-0.5">
                Oldest: {data.items[0].event_label || data.items[0].loan_id}
                {' '}({data.items[0].days_overdue} day{data.items[0].days_overdue === 1 ? '' : 's'})
              </div>
            )}
          </div>
          <ArrowRight size={16} className="text-slate-400" />
        </CardContent>
      </Card>
    </Link>
  );
}
